import { action, observable, makeObservable } from 'mobx';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';
import { Model, IModelAttributes, entity, attribute } from 'Models/Model';
import { bookingType } from 'Models/Enums';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface ICheckInStatusEntityAttributes extends IModelAttributes {
	order: number;
	label: string;
	abbreviation: string;
	includedInCapacity: boolean;
	disembarked: boolean;
	confirmBooking: boolean;
	default: boolean;
	bookingType: bookingType;
	colour: string;
	actionString: string;
	routeId: string;
	checkInStatusHistories: Array<Models.CheckInStatusHistoryEntity | Models.ICheckInStatusHistoryEntityAttributes>;
}

@entity('CheckInStatusEntity', 'Check-in Status')
export default class CheckInStatusEntity extends Model implements ICheckInStatusEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute()
	public order: number;

	@observable
	@attribute()
	public label: string;

	@observable
	@attribute()
	public actionString: string;

	@observable
	@attribute()
	public abbreviation: string;

	@observable
	@attribute()
	public includedInCapacity: boolean;

	@observable
	@attribute()
	public disembarked: boolean;

	@observable
	@attribute()
	public confirmBooking: boolean;

	@observable
	@attribute<CheckInStatusEntity, Enums.bookingType>()
	public bookingType: Enums.bookingType;

	@observable
	@attribute()
	public colour: string;

	@observable
	@attribute()
	public default: boolean;

	@observable
	@attribute()
	public routeId: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public checkInStatusHistories: Models.CheckInStatusHistoryEntity[] = [];

	constructor(attributes?: Partial<ICheckInStatusEntityAttributes>) {
		super(attributes);

		this.checkInStatusHistories = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICheckInStatusEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.order)) {
				this.order = attributes.order;
			}
			if (isNotNullOrUndefined(attributes.label)) {
				this.label = attributes.label;
			}
			if (isNotNullOrUndefined(attributes.abbreviation)) {
				this.abbreviation = attributes.abbreviation;
			}
			if (isNotNullOrUndefined(attributes.includedInCapacity)) {
				this.includedInCapacity = attributes.includedInCapacity;
			}
			if (isNotNullOrUndefined(attributes.disembarked)) {
				this.disembarked = attributes.disembarked;
			}
			if (isNotNullOrUndefined(attributes.confirmBooking)) {
				this.confirmBooking = attributes.confirmBooking;
			}
			if (isNotNullOrUndefined(attributes.default)) {
				this.default = attributes.default;
			}
			if (isNotNullOrUndefined(attributes.bookingType)) {
				this.bookingType = attributes.bookingType;
			}
			if (isNotNullOrUndefined(attributes.colour)) {
				this.colour = attributes.colour;
			}
			if (isNotNullOrUndefined(attributes.actionString)) {
				this.actionString = attributes.actionString;
			}
			if (isNotNullOrUndefined(attributes.routeId)) {
				this.routeId = attributes.routeId;
			}
			if (isNotNullOrUndefined(attributes.checkInStatusHistories)) {
				for (const model of attributes.checkInStatusHistories) {
					if (model instanceof Models.CheckInStatusHistoryEntity) {
						this.checkInStatusHistories.push(model);
					} else {
						this.checkInStatusHistories.push(new Models.CheckInStatusHistoryEntity(model));
					}
				}
			}
		}
	}
}
